import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

// ── Types ─────────────────────────────────────────────────────

export interface ToastItem {
  id: string;
  category: "success" | "warning" | "error";
  message: string;
  durationMs: number;
}

interface ToastContextValue {
  toasts: ToastItem[];
  show: (category: ToastItem["category"], message: string) => void;
  dismiss: (id: string) => void;
}

// ── Context ───────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  show: () => {},
  dismiss: () => {},
});

export function useToast() {
  return useContext(ToastContext);
}

// ── Constants ─────────────────────────────────────────────────

const DURATION: Record<ToastItem["category"], number> = {
  success: 5000,
  warning: 5000,
  error: 10000,
};

const MAX_TOASTS = 5;

// ── Provider ──────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) clearTimeout(timer);
    timers.current.delete(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (category: ToastItem["category"], message: string) => {
      const id =
        typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      const durationMs = DURATION[category];

      setToasts((prev) => {
        const next = [...prev, { id, category, message, durationMs }];
        if (next.length > MAX_TOASTS) {
          const oldest = next.shift()!;
          const t = timers.current.get(oldest.id);
          if (t !== undefined) clearTimeout(t);
          timers.current.delete(oldest.id);
        }
        return next;
      });

      const timer = setTimeout(() => dismiss(id), durationMs);
      timers.current.set(id, timer);
    },
    [dismiss]
  );

  // Expose show() on window for Playwright tests
  useEffect(() => {
    (window as any).__showToast = show;
    return () => {
      delete (window as any).__showToast;
    };
  }, [show]);

  // Clean up all timers on unmount
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => clearTimeout(t));
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, show, dismiss }}>
      {children}
    </ToastContext.Provider>
  );
}
